import { requestSlice } from '../requests.slice';

type CacheItem = { type: TagType; id?: string } | DefaultTags | `${TagType}`;

export const defaultTags = ['NOT_AUTHORIZED', 'SOME_ERROR'] as const;
type DefaultTags = (typeof defaultTags)[number];

export enum TagType {
  CLASS = 'CLASS',
  OBJECT = 'OBJECT',
  OBJECT_LIST = 'OBJECT_LIST',
  DRAFT = 'DRAFT',
  HARD_REFRESH = 'HARD_REFRESH',
}

// Provide

const providesWithErrorTagsIfHasError = (tags: CacheItem[] | null, error: any): CacheItem[] => {
  if (error) {
    if (error?.status === 401) {
      return [...(tags || []), 'NOT_AUTHORIZED'];
    }
    return [...(tags || []), 'SOME_ERROR'];
  }
  return tags;
};

const providesByArg =
  <T extends string>(argKey: T, type: TagType) =>
  (_result: any, error: any, args: { [key in T]: string }): CacheItem[] =>
    providesWithErrorTagsIfHasError(args ? [{ type, id: args[argKey] }] : [], error);

const providesByResponse =
  <T extends string>(responseKey: T, type: TagType) =>
  (result: { [key in T]: string } | null, error: any): CacheItem[] =>
    providesWithErrorTagsIfHasError(result ? [{ type, id: result[responseKey] }] : [], error);

const providesListByIdItemResponse =
  (type: TagType) =>
  (result: { _id: string }[] | null, error: any): CacheItem[] =>
    providesWithErrorTagsIfHasError(result?.length > 0 ? result.map((item) => ({ type, id: item._id })) : [], error);

// Invalidate

const invalidatesIfSuccess = (tags: CacheItem[], error: any): CacheItem[] => (error ? [] : tags);

const invalidatesUnauthorized = (_result: any, error: any): CacheItem[] =>
  invalidatesIfSuccess(['NOT_AUTHORIZED'], error);

const invalidatesDraft = (_result: any, error: any): CacheItem[] => invalidatesIfSuccess([TagType.DRAFT], error);

const invalidatesByArg =
  <T extends string>(argKey: T, type: TagType) =>
  (_result: any, error: any, args: { [key in T]: string }): CacheItem[] =>
    invalidatesIfSuccess(args ? [{ type, id: args[argKey] }] : [], error);

const invalidatesByResponse =
  <T extends string>(responseKey: string, type: TagType) =>
  (result: { [key in T]: string } | null, error: any): CacheItem[] =>
    invalidatesIfSuccess(result ? [{ type, id: result[responseKey] }] : [], error);

export const TagHelpers = {
  /**
   * Define tipos de tag.\
   * Tipos de tag devem ser definidos antes de ser utilizados nas definições dos endpoints (por meio do atributo `providesTags` e `invalidatesTags`)
   */
  defineTagTypes: (tags: string[]): void => {
    requestSlice.enhanceEndpoints({ addTagTypes: tags });
  },
  invalidatesIfSuccess,
  invalidatesUnauthorized,
  invalidatesDraft,
  invalidatesByArg,
  invalidatesByResponse,
  providesWithErrorTagsIfHasError,
  providesByArg,
  providesByResponse,
  providesListByIdItemResponse,
};
